/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { HydratedRouter } from 'react-router/dom'
import { startTransition, StrictMode } from 'react'
import { hydrateRoot } from 'react-dom/client'
import * as Sentry from '@sentry/remix'
import { MuiProvider } from './lib/MuiProvider'
import { initStatsig } from './lib/statsig'
import { replayIntegration } from '@sentry/remix'
import { shouldDropSentryTransaction } from './utils/function'
import { getRouteSpan, setRouteSpan } from './lib/sentryTrace'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const isDev = import.meta.env.VITE_SS_ENV === 'development'

const TRACE_PROPAGATION_TARGETS = ['localhost', 'booksolo.co', 'secretspa.io']

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    enabled: !isDev,
    environment: import.meta.env.VITE_SS_ENV,
    tracesSampleRate: isDev ? 1.0 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    beforeSendTransaction(event) {
      if (shouldDropSentryTransaction(event)) return null
      return event
    },
  })

  // Without browserTracingIntegration the propagation context (and its traceId)
  // is set once on init and never reset, so every span and outgoing request
  // shares the same trace ID for the entire page session.
  // The pageload span stays open until the first navigation, so initial
  // fetches nest underneath it.
  setRouteSpan(Sentry.startInactiveSpan({
    name: window.location.pathname,
    op: 'pageload',
    forceTransaction: true,
  }))

  window.addEventListener('beforeunload', () => getRouteSpan()?.end())

  // Propagate sentry-trace + baggage on outgoing fetch requests to our backends.
  // Each request creates an http.client child span under the current route span
  // so backend transactions nest under the route that triggered them.
  const _fetch = window.fetch.bind(window)
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
    if (!TRACE_PROPAGATION_TARGETS.some(t => url.includes(t))) {
      return _fetch(input, init)
    }

    const routeSpan = getRouteSpan()
    const httpSpan = Sentry.startInactiveSpan({
      name: `${init?.method || 'GET'} ${new URL(url, window.location.origin).pathname}`,
      op: 'http.client',
      parentSpan: routeSpan,
    })

    const traceHeaders = Sentry.getTraceData({ span: routeSpan })
    const headers = new Headers(init?.headers)
    for (const [key, value] of Object.entries(traceHeaders)) {
      if (!headers.has(key)) headers.set(key, value)
    }

    return _fetch(input, { ...init, headers }).then(
      (response) => {
        httpSpan?.setAttribute('http.response.status_code', response.status)
        httpSpan?.end()
        return response
      },
      (error) => {
        httpSpan?.setStatus({ code: 2, message: 'internal_error' })
        httpSpan?.end()
        throw error
      },
    )
  }
}

initStatsig()

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <MuiProvider>
        <HydratedRouter />
      </MuiProvider>
    </StrictMode>,
  )
})
